export interface BackendTimecode {
    frames: number;
    type: string;
    frameRate: BackendFrameRate;
}

export type MediaObjectType =
    | 'RAW'
    | 'PROXY'
    | 'EDL'
    | 'SHOTSTITCH'
    | 'STITCH'
    | 'THUMB'
    | 'THUMB_SHOT'
    | 'THUMB_SIXTY'
    | 'WAVEFORM'
    | 'AUDIO_MONO'
    | 'AUDIO_SPEECH'
    | 'VOLDEMORT_XML'
    | 'ATTACHMENT'
    | 'HDR_PROXY'
    | 'MEZZANINE_PROXY'
    | string;

export type MediaObjectQuality = 'LOW' | 'MEDIUM' | 'HIGH' | 'ULTRA' | string;

export interface MediaIdentifier {
    id: string;
    mediaObjectId: string;

    type: MediaObjectType;
    mimeType: string;
    quality: MediaObjectQuality;

    duration: number;
    frames: number;
    frameRate: FrameRate;

    width: number;
    height: number;

    href: string;
}

export type MediaIdentifierQualities = Record<
    MediaObjectQuality,
    MediaIdentifier
>;

export type MediaIdentifiers = Record<
    MediaObjectType,
    Partial<MediaIdentifierQualities>
>;

export interface BackendMediaInfo {
    creationTime: number;
    dominatingTrackType: TrackType;
    duration: number;
    fingerprints: string[];
    frames: number;
    isAAFSequence: boolean;
    mediaObjectType: TrackType;
    mimeType: string;
    rotation: number;
    sampleRate: BackendFrameRate;
    size: number;
    time_mediainfo: number;
    time_total: number;
    timecode: number;
    timecodes: BackendTimecode[];
    tracks: BackendTrack[];
    width: number;
    height: number;
}

export interface BackendMaterialInfo {
    onAlternateBackup?: boolean;
    onArchive1?: boolean;
    onArchive2?: boolean;
    onArchive3?: boolean;
    onBackup?: boolean;
    onFlow?: boolean;
    onHotStorage?: boolean;
    online?: boolean;
    shouldBeOnBackup?: boolean;
    shouldBeOnFlow?: boolean;
    shouldBeOnHotStorage?: boolean;
    size?: number;
    verificationStatus?: string;
}

export interface BackendMediaObjectInstance {
    id: string;
    // sourceMediaObjectInstanceId: string;
    mediaObjectId: string;
    type: MediaObjectType;
    mimeType: string;
    quality?: MediaObjectQuality;
    size: number;
    duration: number; // Duration in seconds
    frames: number; // Duration in frames
    tracks: BackendTrack[];
    href: string;
    mediaInfo?: BackendMediaInfo;
    materialInfo?: BackendMaterialInfo;
}

export interface BackendMediaObject {
    id?: string;
    name: string;
    realName: string;
    duration: number; // Duration in frames
    durationSeconds?: number; // Duration in seconds
    mediaObjectInstances?: BackendMediaObjectInstance[];
    sampleRate?: BackendFrameRate;
}

// ####################### TRACKS

export interface BackendFrameRate {
    denumerator: number;
    numerator: number;
    drop_frame: boolean;
}

export interface FrameRate {
    denumerator: number;
    numerator: number;
    dropFrames: boolean;
    fps: string;
}

export type TrackType =
    | 'VIDEO'
    | 'AUDIO'
    | 'SUBTITLE'
    | 'DATA'
    | 'IMAGE'
    | 'DOCUMENT'
    | string;

export interface Track {
    id: string;
    src: string;
    type: TrackType;

    productionId: string;
    mediaObjectId: string;
    moiId: string;

    frameRate: FrameRate;

    start: number;
    end: number;
    duration: number;
    stitchCount: number;

    height: number;
    width: number;
    stitchX: number;
    stitchY: number;
}

export interface BackendTrack {
    bits_per_sample: number;
    channels: number;
    created: number;
    createdBy: string;
    duration: number;
    end: number;
    file_index: number;
    // fingerprints: [];

    fps: { denumerator: number; drop_frame: boolean; numerator: number };

    height: number;
    href: string;
    hrefs: {
        data: string;
        download: string;
        mai: string;
    };
    id: string;
    index: number;
    kBitRate: number;
    lastUpdated: number;
    materialEnd: number;
    materialStart: number;
    mediaObjectInstanceId: string;
    mediaObjectTrackId: string;
    mobile: boolean;
    modifiedBy: string;
    number: number;
    objectType: MediaObjectType;
    samplingFrequency: number;
    segmentDuration: number;
    size: number;
    skip: boolean;
    start: number;
    stitchCount: number;
    stitchX: number;
    stitchY: number;
    subsampling: string;
    timecodeOffset: number;
    type: TrackType;
    uuid: string;
    version: number;
    width: number;
}

//~ Constants

export const MEDIA_IDENTIFIER_NAMES: Record<MediaObjectType, string> = {
    RAW: 'RAW',
    PROXY: 'PROXY',
    EDL: 'EDL',
    SHOTSTITCH: 'SHOTSTITCH',
    STITCH: 'STITCH',
    THUMB: 'THUMB',
    THUMB_SHOT: 'THUMB_SHOT',
    THUMB_SIXTY: 'THUMB_SIXTY',
    WAVEFORM: 'WAVEFORM',
    AUDIO_MONO: 'AUDIO_MONO',
    AUDIO_SPEECH: 'AUDIO_SPEECH',
    VOLDEMORT_XML: 'VOLDEMORT_XML',
    ATTACHMENT: 'ATTACHMENT',
    HDR_PROXY: 'HDR_PROXY',
    MEZZANINE_PROXY: 'MEZZANINE_PROXY',
};

export const DEFAULT_FRAME_RATE: FrameRate = {
    denumerator: 0,
    numerator: 0,
    dropFrames: false,
    fps: '',
};

export const DEFAULT_MEDIA_IDENTIFIER: MediaIdentifier = {
    id: '',
    mediaObjectId: '',
    mimeType: '',
    quality: '',
    href: '',
    type: '',
    duration: 0,
    frames: 0,
    frameRate: DEFAULT_FRAME_RATE,
    width: 1920,
    height: 1080,
};

export const DEFAULT_MEDIA_IDENTIFIERS: MediaIdentifiers = Object.entries(
    MEDIA_IDENTIFIER_NAMES,
).reduce((acc, [key]) => {
    acc[key as MediaObjectType] = {
        LOW: DEFAULT_MEDIA_IDENTIFIER,
        MEDIUM: DEFAULT_MEDIA_IDENTIFIER,
        HIGH: DEFAULT_MEDIA_IDENTIFIER,
        ULTRA: DEFAULT_MEDIA_IDENTIFIER,
    };

    return acc;
}, {} as MediaIdentifiers);

export const DEFAULT_TRACK: Track = {
    id: '',
    src: '',
    type: '',
    productionId: '',
    mediaObjectId: '',
    moiId: '',
    frameRate: DEFAULT_FRAME_RATE,
    start: 0,
    end: 0,
    duration: 0,
    stitchCount: 0,
    height: 0,
    width: 0,
    stitchX: 0,
    stitchY: 0,
};
